/**
 * 用户详情抽屉（Alice 的 .users-detail-pane）：概览、请求记录、花瓣流水、账户与权限四个标签。
 *
 * 背景：Alice 把用户资产、模型消耗、余额流水、Key、封禁放在同一个右侧抽屉里，客服不用跳页。本仓的用户就是账号，
 *   余额与 Key 直接挂在账号上，数据来自三个服务：封禁、内部标记、地域豁免走账号服务，
 *   余额、Key、统计、流水、调账走控制面（usersApi 已按路径分好）。
 * 设计意图：
 *   - 每个标签页按需加载（没打开就不请求），同一用户切标签不重复请求；换用户时全部重置，旧请求结果丢弃。
 *   - 抽屉内持有一份用户行的本地副本：封禁、内部、地域豁免成功后只改副本并通知列表刷新，
 *     用户即使因筛选掉出当前列表页也不会让抽屉里的状态回退。
 *   - 调账的 ref 在打开用户时生成一次，成功后换新；失败重试沿用，后端据此幂等，不会记两次。
 *   - 有偿余额的大额发放（超过 1 万花瓣）由后端要求二次确认：收到 large_normal_grant_needs_ack 后弹确认框，
 *     确认后用同一个 ref 再提交一次。
 * 关键约束：写操作要 admin（按钮对 support 隐藏，后端为准）；赠送花瓣只能增加，所以勾选「赠送」时扣减按钮禁用；
 *   Esc 关抽屉，但有弹窗打开时只关弹窗。
 */
import { IconX } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import type { ChartConfiguration } from 'chart.js'
import { UNITS_PER_PETAL } from '@ap/api-client/money'
import { ApiError } from '@ap/api-client/http'
import { usersApi, type LedgerFamily, type UserRow } from '@ap/api-client/admin-users'
import { Link, fmtDateTime, useToast } from '@ap/ui'
import { Pager } from '../kit.tsx'
import { ChartCanvas, chartOpts } from '../biz-kit.tsx'
import { PILL, Pill } from '../kit-runtime2.tsx'
import type { AG_users as G0 } from '@ap/i18n/admin-users'
import { Check, IntInput, UModal, compact, fieldStyle, newRef, useInt, useQuery, useU } from '../kit-users.tsx'
import { useAdmin } from '../../session.tsx'

type Tab = 'overview' | 'activity' | 'ledger' | 'account'
const LEDGER_PAGE = 20

type ReasonJob = { kind: 'ban' | 'unban' | 'internal' | 'geo'; on?: boolean }

export function UserDrawer({ user, onClose, onChanged }: { user: UserRow | null; onClose: () => void; onChanged: () => void }) {
  const { t, G, err, pet, num, signed, locale } = useU()
  const D = G.users.drawer
  const int = useInt()
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const [row, setRow] = useState<UserRow | null>(user)
  const [tab, setTab] = useState<Tab>('overview')
  const [days, setDays] = useState(7)
  const [family, setFamily] = useState<LedgerFamily>('')
  const [offset, setOffset] = useState(0)
  const id = user?.account_id ?? ''

  // 换用户：重置本地副本与各标签的状态
  useEffect(() => {
    setRow(user)
    setTab('overview')
    setDays(7)
    setFamily('')
    setOffset(0)
  }, [user?.account_id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Esc 关抽屉；弹窗打开时 Esc 属于弹窗
  useEffect(() => {
    if (!user) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.modal-mask.open')) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [user, onClose])

  const open = user !== null
  const wallet = useQuery(() => usersApi.wallet(id), [id], open)
  const stats = useQuery(() => usersApi.stats(id, days), [id, days], open && (tab === 'overview' || tab === 'activity'))
  const ledger = useQuery(() => usersApi.ledger(id, family, LEDGER_PAGE, offset), [id, family, offset], open && tab === 'ledger')
  const keys = useQuery(() => usersApi.keys(id), [id], open && tab === 'account')
  const bans = useQuery(() => usersApi.bans(id), [id], open && tab === 'account')

  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [asBound, setAsBound] = useState(false)
  const [ref, setRef] = useState(newRef)
  const [busy, setBusy] = useState(false)
  const [large, setLarge] = useState<{ sign: 1 | -1 } | null>(null)
  const [job, setJob] = useState<ReasonJob | null>(null)

  useEffect(() => {
    setAmount('')
    setNote('')
    setAsBound(false)
    setRef(newRef())
    setLarge(null)
    setJob(null)
  }, [id])

  const charts = useMemo(() => buildCharts(stats.data, D, t), [stats.data, D, t])

  if (!user || !row) return null
  const name = row.display_name || row.email || row.account_id
  const balanceUnits = wallet.data?.balance_units ?? row.balance_units

  const adjust = async (sign: 1 | -1, confirmLarge: boolean) => {
    const n = Number(amount)
    if (!Number.isInteger(n) || n <= 0) {
      toast.error(t(D.amountInvalid))
      return
    }
    setBusy(true)
    try {
      const r = await usersApi.recharge(id, {
        amount_petals: sign * n,
        note: note.trim(),
        as_bound: asBound,
        confirm_large_normal_grant: confirmLarge,
        ref,
      })
      toast.ok(t(r.created ? D.adjustDone : D.adjustRepeat, { v: num(r.balance_units) }))
      setLarge(null)
      setAmount('')
      setNote('')
      setRef(newRef())
      wallet.reload()
      ledger.reload()
      onChanged()
    } catch (e) {
      if (e instanceof ApiError && e.code === 'large_normal_grant_needs_ack' && !confirmLarge) {
        setLarge({ sign })
      } else {
        toast.error(err(e))
      }
    } finally {
      setBusy(false)
    }
  }

  const runReason = async (reason: string) => {
    if (!job) return
    if (job.kind === 'ban') {
      await usersApi.ban(id, reason)
      setRow({ ...row, is_banned: true, status: 'locked' })
    } else if (job.kind === 'unban') {
      await usersApi.unban(id, reason)
      setRow({ ...row, is_banned: false, status: 'active' })
    } else if (job.kind === 'internal') {
      await usersApi.setInternal(id, !!job.on, reason)
      setRow({ ...row, is_internal: !!job.on })
    } else {
      await usersApi.setGeoExempt(id, !!job.on, reason)
      setRow({ ...row, geo_exempt: !!job.on })
    }
    toast.ok(t(D.changed))
    setJob(null)
    bans.reload()
    onChanged()
  }

  const tabs: [Tab, string][] = [
    ['overview', D.tabOverview],
    ['activity', D.tabActivity],
    ['ledger', D.tabLedger],
    ['account', D.tabAccount],
  ].map(([k, e]) => [k as Tab, t(e as typeof D.tabOverview)])

  const s = stats.data
  const sum = ledger.data?.summary

  return (
    <>
      <div className="user-detail-scrim" style={{ display: 'block' }} onClick={onClose} />
      <aside className="users-detail-pane" aria-label={t(D.label)}>
        <div className="d-header">
          <div className="d-avatar">{(name.trim()[0] ?? '?').toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="mono" title={row.account_id}>
                {row.email || row.account_id.slice(0, 8)}
              </span>
              {row.paid_units > 0 && <Pill tone={PILL.green}>{t(G.users.badgePaid)}</Pill>}
              {row.is_banned && <Pill tone={PILL.red}>{t(G.users.badgeBanned)}</Pill>}
              {row.is_internal && <Pill tone={PILL.blue}>{t(G.users.badgeInternal)}</Pill>}
              {row.geo_exempt && <Pill tone={PILL.purple}>{t(G.users.badgeGeo)}</Pill>}
            </div>
          </div>
          <div className="d-header-balance">
            <div className="d-header-balance-label">{t(D.balance)}</div>
            <div className="d-header-balance-value">{pet(balanceUnits)}</div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ padding: '6px 9px' }} aria-label={t(G.common.close)}>
            <IconX size={14} />
          </button>
        </div>
        <div className="user-detail-tabs" role="tablist">
          {tabs.map(([k, label]) => (
            <button key={k} className={`user-detail-tab${tab === k ? ' active' : ''}`} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </div>

        <div className="d-body">
          {/* ---------------- 概览 */}
          {tab === 'overview' && (
            <>
              <div className="d-section">
                <div className="d-section-title">{t(D.usageStats)}</div>
                <div className="user-detail-stats">
                  <Mini label={t(D.totalCost)} value={s ? pet(s.total_cost_units) : '—'} color="var(--error-text)" />
                  <Mini label={t(D.totalRequests)} value={s ? int(s.total_requests) : '—'} />
                  <Mini label={t(D.inputTokens)} value={s ? compact(s.total_input_tokens) : '—'} />
                  <Mini label={t(D.outputTokens)} value={s ? compact(s.total_output_tokens) : '—'} />
                </div>
                {stats.error != null && <div className="ops-note" style={{ marginTop: 10 }}>{t(G.common.loadFailed, { msg: err(stats.error) })}</div>}
              </div>
              <div className="d-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div className="d-section-title" style={{ marginBottom: 0 }}>
                    {t(D.trend)}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[7, 14, 30].map((n) => (
                      <button
                        key={n}
                        className={`btn btn-secondary btn-sm${days === n ? ' active' : ''}`}
                        style={{ padding: '3px 8px', fontSize: 11, ...(days === n ? { color: 'var(--accent)', borderColor: 'var(--accent)' } : null) }}
                        onClick={() => setDays(n)}
                      >
                        {t(D.days, { n })}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="user-detail-chart-grid">
                  {charts ? (
                    <>
                      <div className="user-detail-chart">
                        <ChartCanvas config={charts.cost} label={t(D.chartCost)} />
                      </div>
                      <div className="user-detail-chart">
                        <ChartCanvas config={charts.tokens} label={t(D.chartTokens)} />
                      </div>
                      <div className="user-detail-chart">
                        <ChartCanvas config={charts.requests} label={t(D.chartRequests)} />
                      </div>
                    </>
                  ) : (
                    <div className="u2-note">{stats.loading ? t(G.common.loading) : t(D.noData)}</div>
                  )}
                </div>
              </div>
              <div className="d-section">
                <div className="d-section-title">{t(D.byModel)}</div>
                <div className="user-detail-table-wrap" style={{ fontSize: 12 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t(D.colModel)}</th>
                        <th>{t(D.colRequests)}</th>
                        <th>{t(D.colTokens)}</th>
                        <th>{t(D.colCost)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s && s.by_model.length === 0 && (
                        <tr>
                          <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
                            {t(D.noData)}
                          </td>
                        </tr>
                      )}
                      {s?.by_model.map((m) => (
                        <tr key={m.model}>
                          <td className="mono">{m.model}</td>
                          <td>{int(m.requests)}</td>
                          <td>{compact(m.tokens)}</td>
                          <td>{pet(m.cost_units)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ---------------- 请求记录 */}
          {tab === 'activity' && (
            <div className="d-section">
              <div className="d-section-title">{t(D.recent)}</div>
              <div className="user-detail-table-wrap" style={{ fontSize: 12 }}>
                <table>
                  <thead>
                    <tr>
                      <th>{t(D.colTime)}</th>
                      <th>{t(D.colModel)}</th>
                      <th>{t(D.colStatus)}</th>
                      <th>{t(D.colTokens)}</th>
                      <th>{t(D.colCost)}</th>
                      <th>{t(D.colDuration)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s && s.recent.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
                          {t(D.noData)}
                        </td>
                      </tr>
                    )}
                    {s?.recent.map((r) => (
                      <tr key={r.request_id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at, locale)}</td>
                        <td className="mono">{r.model}</td>
                        <td title={r.error_code || undefined}>
                          <Pill tone={r.status === 'ok' ? PILL.green : PILL.red}>{r.status === 'ok' ? 'OK' : r.error_code || r.status}</Pill>
                        </td>
                        <td>
                          {compact(r.input_tokens)} / {compact(r.output_tokens)}
                        </td>
                        <td>{pet(r.cost_units)}</td>
                        <td>{r.duration_ms >= 1000 ? `${(r.duration_ms / 1000).toFixed(1)}s` : `${r.duration_ms}ms`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {stats.loading && !s && <div className="u2-note">{t(G.common.loading)}</div>}
            </div>
          )}

          {/* ---------------- 花瓣流水 */}
          {tab === 'ledger' && (
            <>
              <div className="d-section">
                <div className="d-section-title">{t(D.ledgerSummary)}</div>
                <div className="user-detail-stats">
                  <Mini label={t(D.ledgerIn)} value={sum ? num(sum.total_in_units) : '—'} color="var(--success-text)" />
                  <Mini label={t(D.ledgerOut)} value={sum ? num(sum.total_out_units) : '—'} color="var(--error-text)" />
                  <Mini label={t(D.ledgerNet)} value={sum ? signed(sum.net_units) : '—'} />
                  <Mini
                    label={t(D.ledgerPaid)}
                    value={sum ? num(sum.paid_in_units) : '—'}
                    title={t(D.ledgerPaidTip)}
                    onClick={() => {
                      setFamily('money')
                      setOffset(0)
                    }}
                  />
                </div>
                {sum && sum.unrecorded_delta_units !== 0 && (
                  <div className="ops-note" style={{ marginTop: 10 }}>
                    {t(D.unrecorded, { n: signed(sum.unrecorded_delta_units) })}
                  </div>
                )}
                {ledger.error != null && <div className="ops-note" style={{ marginTop: 10 }}>{t(G.common.loadFailed, { msg: err(ledger.error) })}</div>}
              </div>
              <div className="d-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
                  <div className="d-section-title" style={{ marginBottom: 0 }}>
                    {t(D.ledgerDetail)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {(
                      [
                        ['', D.familyAll],
                        ['money', D.familyMoney],
                        ['usage', D.familyUsage],
                      ] as [LedgerFamily, typeof D.familyAll][]
                    ).map(([k, label]) => (
                      <button
                        key={k}
                        className="btn btn-secondary btn-sm"
                        style={{ padding: '3px 8px', fontSize: 11, ...(family === k ? { color: 'var(--accent)', borderColor: 'var(--accent)' } : null) }}
                        onClick={() => {
                          setFamily(k)
                          setOffset(0)
                        }}
                      >
                        {t(label)}
                      </button>
                    ))}
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{ledger.data ? t(D.count, { n: ledger.data.total }) : ''}</span>
                  </div>
                </div>
                <div className="user-detail-table-wrap" style={{ fontSize: 12 }}>
                  <table className="ledger-table">
                    <thead>
                      <tr>
                        <th>{t(D.colTime)}</th>
                        <th>{t(D.colType)}</th>
                        <th>{t(D.colAmount)}</th>
                        <th>{t(D.colAfter)}</th>
                        <th>{t(D.colNote)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.data && ledger.data.entries.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
                            {t(D.noData)}
                          </td>
                        </tr>
                      )}
                      {ledger.data?.entries.map((e) => {
                        const money = e.family !== 'usage'
                        const kind = D.kinds[e.kind]
                        const channel = e.channel ? D.channels[e.channel] : undefined
                        return (
                          <tr key={e.id} className={money ? `ledger-row-${e.family}` : undefined}>
                            <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.occurred_at, locale)}</td>
                            <td>
                              <span className={`ledger-type-badge ledger-type-${e.family}`}>{kind ? t(kind) : e.kind}</span>
                              {channel && <div className="u2-note" style={{ marginTop: 2 }}>{t(channel)}</div>}
                            </td>
                            <td style={{ color: e.direction === 'in' ? 'var(--success-text)' : 'var(--error-text)', whiteSpace: 'nowrap' }}>
                              {e.direction === 'in' ? '+' : '−'}
                              {num(e.amount_units)}
                              {e.bonus_units > 0 && e.paid_units > 0 && (
                                <div className="u2-note" style={{ marginTop: 2 }}>
                                  {num(e.paid_units)} + {num(e.bonus_units)}
                                </div>
                              )}
                            </td>
                            <td>{e.balance_after_units === null ? '—' : num(e.balance_after_units)}</td>
                            <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.note || e.model || e.ref}>
                              {e.model ?? (e.note || e.ref)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: '10px 0 0', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Pager offset={offset} limit={LEDGER_PAGE} total={ledger.data?.total ?? 0} onChange={setOffset} />
                </div>
              </div>
            </>
          )}

          {/* ---------------- 账户与权限 */}
          {tab === 'account' && (
            <>
              {isAdmin && (
                <div className="d-section">
                  <div className="d-section-title">{t(D.adjustTitle)}</div>
                  <div className="grant-row">
                    <IntInput className="grant-input" placeholder={t(D.amountPh)} value={amount} onChange={setAmount} />
                    <input className="grant-input" type="text" maxLength={200} placeholder={t(D.notePh)} value={note} onChange={(e) => setNote(e.target.value)} />
                    <button className="btn btn-success btn-sm" disabled={busy} onClick={() => adjust(1, false)}>
                      {t(D.add)}
                    </button>
                    <button
                      className="btn btn-sm"
                      disabled={busy || asBound}
                      title={asBound ? t(D.asBoundHint) : undefined}
                      onClick={() => adjust(-1, false)}
                      style={{ color: '#fff', background: 'var(--error-text)', borderColor: 'var(--error-text)', opacity: busy || asBound ? 0.5 : 1 }}
                    >
                      {t(D.deduct)}
                    </button>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Check checked={asBound} onChange={setAsBound} hint={t(D.asBoundHint)}>
                      {t(D.asBound)}
                    </Check>
                  </div>
                </div>
              )}
              <div className="d-section">
                <div className="d-section-title">{t(D.keys)}</div>
                {keys.error != null && <div className="u2-note">{t(G.common.loadFailed, { msg: err(keys.error) })}</div>}
                {keys.data && keys.data.keys.length === 0 && <div className="u2-note">{t(D.keysEmpty)}</div>}
                {keys.data?.keys.map((k) => (
                  <div className="u2-key-row" key={k.id}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {k.name || '—'} {k.revoked_at && <Pill tone={PILL.gray}>{t(D.keyRevoked)}</Pill>}
                      </div>
                      <div className="mono" style={{ color: 'var(--text-tertiary)' }}>
                        {k.display}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                      <div>
                        {k.quota_limit_units
                          ? t(D.keyQuota, { used: num(k.quota_used_units), limit: num(k.quota_limit_units) })
                          : t(D.keyUnlimited)}
                      </div>
                      <div style={{ color: 'var(--text-tertiary)' }}>
                        {k.last_used_at ? t(D.keyLastUsed, { t: fmtDateTime(k.last_used_at, locale) }) : t(D.keyNever)}
                      </div>
                    </div>
                  </div>
                ))}
                {keys.loading && !keys.data && <div className="u2-note">{t(G.common.loading)}</div>}
              </div>
              <div className="d-section">
                <div className="d-section-title">{t(D.controls)}</div>
                <div className="user-detail-controls">
                  <Control
                    title={t(D.accountStatus)}
                    status={<span style={{ color: row.is_banned ? 'var(--error-text)' : 'var(--success-text)' }}>{t(row.is_banned ? D.statusBanned : D.statusActive)}</span>}
                    action={
                      isAdmin && (
                        <button
                          className={`btn btn-sm ${row.is_banned ? 'btn-success' : 'btn-danger'}`}
                          onClick={() => setJob({ kind: row.is_banned ? 'unban' : 'ban' })}
                        >
                          {t(row.is_banned ? D.unban : D.ban)}
                        </button>
                      )
                    }
                  />
                  <Control
                    title={t(D.memberType)}
                    status={t(row.is_internal ? D.typeInternal : D.typeExternal)}
                    action={
                      isAdmin && (
                        <button className="btn btn-secondary btn-sm" onClick={() => setJob({ kind: 'internal', on: !row.is_internal })}>
                          {t(row.is_internal ? D.unmarkInternal : D.markInternal)}
                        </button>
                      )
                    }
                  />
                  <Control
                    title={t(D.geo)}
                    status={t(row.geo_exempt ? D.geoOn : D.geoOff)}
                    action={
                      isAdmin && (
                        <button className="btn btn-secondary btn-sm" onClick={() => setJob({ kind: 'geo', on: !row.geo_exempt })}>
                          {t(row.geo_exempt ? D.geoDisable : D.geoEnable)}
                        </button>
                      )
                    }
                  />
                  <Control
                    title={t(D.accountType)}
                    status={t(D.accountTypeValue)}
                    action={
                      <Link href={`/accounts/${row.account_id}`} className="btn btn-secondary btn-sm">
                        {t(G.users.accountsLink)}
                      </Link>
                    }
                  />
                </div>
                <div className="u2-note">{t(D.flagNote)}</div>
              </div>
              <div className="d-section">
                <div className="d-section-title">{t(D.banHistory)}</div>
                {bans.error != null && <div className="u2-note">{t(G.common.loadFailed, { msg: err(bans.error) })}</div>}
                {bans.data && bans.data.bans.length === 0 && <div className="u2-note">{t(D.banHistoryEmpty)}</div>}
                {bans.data?.bans.map((b) => (
                  <div className="u2-ban-row" key={b.id}>
                    <div>
                      <Pill tone={b.lifted_at ? PILL.gray : PILL.red}>{t(b.lifted_at ? D.banLifted : D.banActive)}</Pill>{' '}
                      <span style={{ color: 'var(--text-tertiary)' }}>
                        {fmtDateTime(b.banned_at, locale)}
                        {b.lifted_at ? ` → ${fmtDateTime(b.lifted_at, locale)}` : ''}
                      </span>
                    </div>
                    <div>{b.reason}</div>
                    <div style={{ color: 'var(--text-tertiary)' }}>
                      {t(D.banBy, { id: b.banned_by.slice(0, 8) })}
                      {b.lifted_at && b.lift_reason ? ` · ${t(D.banLiftReason, { r: b.lift_reason })}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </aside>

      <ReasonDialog
        job={job}
        who={name}
        onClose={() => setJob(null)}
        onSubmit={runReason}
      />
      <UModal
        open={large !== null}
        title={t(D.largeTitle)}
        onClose={() => setLarge(null)}
        busy={busy}
        width={440}
        dismissOnMask
        footer={
          <>
            <button className="btn btn-secondary" disabled={busy} onClick={() => setLarge(null)}>
              {t(G.common.cancel)}
            </button>
            <button className="btn btn-primary" disabled={busy} onClick={() => large && adjust(large.sign, true)}>
              {t(G.common.confirm)}
            </button>
          </>
        }
      >
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>{t(D.largeBody)}</div>
      </UModal>
    </>
  )
}

function Mini({ label, value, color, title, onClick }: { label: string; value: string; color?: string; title?: string; onClick?: () => void }) {
  return (
    <div className="user-detail-stat" style={onClick ? { cursor: 'pointer' } : undefined} title={title} onClick={onClick}>
      <div className="user-detail-stat-label">{label}</div>
      <div className="user-detail-stat-value" style={color ? { color } : undefined}>
        {value}
      </div>
    </div>
  )
}

function Control({ title, status, action }: { title: string; status: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="user-detail-control">
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{title}</div>
        <span style={{ fontSize: 12 }}>{status}</span>
      </div>
      {action}
    </div>
  )
}

/** 封禁、解封、内部标记、地域豁免共用的「填原因再确认」弹窗：原因 2 到 500 字，失败留在弹窗里。 */
function ReasonDialog({ job, who, onClose, onSubmit }: { job: ReasonJob | null; who: string; onClose: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const { t, G, err } = useU()
  const D = G.users.drawer
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (job) {
      setReason('')
      setError('')
    }
  }, [job])
  if (!job) return null
  const title =
    job.kind === 'ban' ? D.banTitle : job.kind === 'unban' ? D.unbanTitle : job.kind === 'internal' ? D.internalTitle : D.geoTitle
  const lead = job.kind === 'ban' ? D.banBody : job.kind === 'unban' ? D.unbanBody : null
  const label =
    job.kind === 'ban'
      ? D.ban
      : job.kind === 'unban'
        ? D.unban
        : job.kind === 'internal'
          ? job.on
            ? D.markInternal
            : D.unmarkInternal
          : job.on
            ? D.geoEnable
            : D.geoDisable
  const valid = reason.trim().length >= 2
  const submit = async () => {
    if (!valid) return
    setBusy(true)
    setError('')
    try {
      await onSubmit(reason.trim())
    } catch (e) {
      console.error('[g2] 账号管控失败', e)
      setError(err(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <UModal
      open
      title={t(title, { who })}
      onClose={onClose}
      busy={busy}
      width={460}
      footer={
        <>
          <button className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t(G.common.cancel)}
          </button>
          <button className={`btn ${job.kind === 'ban' ? 'btn-danger' : 'btn-primary'}`} disabled={busy || !valid} onClick={submit}>
            {t(label)}
          </button>
        </>
      }
    >
      {lead && <div className="ops-note" style={{ marginTop: 0 }}>{t(lead)}</div>}
      {error && <div className="ops-note" style={{ color: 'var(--error-text)' }}>{error}</div>}
      <div className="form-group">
        <label>{t(G.common.reason)}</label>
        <textarea maxLength={500} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...fieldStyle, width: '100%' }} />
        <div className="u2-note" style={{ marginTop: 4 }}>
          {t(G.common.reasonHint)}
        </div>
      </div>
    </UModal>
  )
}

/** 三张趋势图：每日消耗（花瓣）、每日 Token（输入与输出堆叠）、每日请求数。 */
function buildCharts(
  stats: { daily: { date: string; requests: number; cost_units: number; input_tokens: number; output_tokens: number }[] } | null,
  D: typeof G0.users.drawer,
  t: ReturnType<typeof useU>['t'],
): { cost: ChartConfiguration; tokens: ChartConfiguration; requests: ChartConfiguration } | null {
  if (!stats || stats.daily.length === 0) return null
  const labels = stats.daily.map((d) => d.date.slice(5))
  const colors = { accent: 'rgba(25,156,255,0.75)', green: 'rgba(16,185,129,0.75)', amber: 'rgba(245,158,11,0.75)' }
  const titled = (text: string) => {
    const o = chartOpts()
    o.plugins.legend = { display: false }
    o.plugins.title = { display: true, text, align: 'start', font: { size: 11 }, color: '#6b7280' }
    return o
  }
  const single = (label: string, data: number[], color: string): ChartConfiguration => ({
    type: 'bar',
    data: { labels, datasets: [{ label, data, backgroundColor: color, borderRadius: 4 }] },
    options: titled(label) as never,
  })
  const tokenOpts = chartOpts()
  tokenOpts.plugins.title = { display: true, text: t(D.chartTokens), align: 'start', font: { size: 11 }, color: '#6b7280' }
  tokenOpts.scales.x = { ...tokenOpts.scales.x, stacked: true }
  tokenOpts.scales.y = { ...tokenOpts.scales.y, stacked: true }
  return {
    cost: single(t(D.chartCost), stats.daily.map((d) => d.cost_units / UNITS_PER_PETAL), colors.accent),
    tokens: {
      type: 'bar',
      data: {
        labels,
        datasets: [
          { label: t(D.inputLabel), data: stats.daily.map((d) => d.input_tokens), backgroundColor: colors.green, borderRadius: 0 },
          { label: t(D.outputLabel), data: stats.daily.map((d) => d.output_tokens), backgroundColor: colors.amber, borderRadius: 4 },
        ],
      },
      options: tokenOpts as never,
    },
    requests: single(t(D.chartRequests), stats.daily.map((d) => d.requests), colors.accent),
  }
}
